import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { gql, useMutation } from '@apollo/client'
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import clsx from 'clsx'

import { getStrengthCheckMessage } from '../utils'

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword(
    $username: String!
    $key: String!
    $password: String!
  ) {
    resetUserPassword(
      input: { key: $key, login: $username, password: $password }
    ) {
      user {
        email
      }
    }
  }
`

const Reset = () => {
  let navigate = useNavigate()
  let location = useLocation()
  let [searchParams, setSearchParams] = useSearchParams()
  const key = searchParams.get('key')
  const login = searchParams.get('login')

  const [resetUserPassword] = useMutation(RESET_USER_PASSWORD)
  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    criteriaMode: 'all',
  })
  const [resetting, setResetting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [formMessage, setFormMessage] = useState('')

  useEffect(() => {
    if (location.pathname === '/reset') {
      if (!key || !login) {
        navigate('/forgot', { replace: true })
      }
    }
  }, [key, login, navigate, location.pathname])

  const onFormSubmit = async ({ password }) => {
    try {
      setResetting(true)

      await resetUserPassword({
        variables: {
          username: login,
          key: key,
          password: password,
        },
      })

      // Clear the url search params
      setSearchParams()

      setSuccess(true)
    } catch (err) {
      console.error('resetPassword :: err ::', JSON.stringify(err))
      setFormMessage('Unable to set a new password. Please try again!')
      setSuccess(false)
    } finally {
      setResetting(false)
    }
  }

  return (
    <main className="min-h-[calc(100vh-200px)]">
      <div className="container pb-20 pt-2">
        <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
          reset.
        </h1>
        <div className="mx-auto flex w-[315px] flex-col items-center divide-y divide-light-gray rounded-[19px] bg-white py-2 pb-10 shadow-lg [&>*]:px-6">
          {/** Header */}
          <div className="py-4 text-sm font-bold text-flame">
            {success
              ? 'Password reset successfully!'
              : 'Enter your new password below.'}
          </div>

          <div className="w-full">
            <div
              className={clsx(
                'opacity-0 transition-opacity',
                formMessage !== '' && 'mb-5 opacity-100',
                !success && 'text-red-500'
              )}
            >
              {formMessage}
            </div>

            {success ? (
              <p className="mt-6 text-left">
                You may now{' '}
                <Link to="/login" replace className="underline">
                  log in
                </Link>{' '}
                with your new password.
              </p>
            ) : (
              <div className="text-center">
                <form
                  className={clsx('group mb-4 mt-6', resetting && 'resetting')}
                  onSubmit={handleSubmit(onFormSubmit)}
                >
                  <fieldset className="mb-5">
                    <input
                      {...register('password', {
                        required: true,
                        validate: {
                          strengthCheck: (value) => {
                            const { strength, message } =
                              getStrengthCheckMessage(value)

                            return strength === 'strong' ? true : message
                          },
                        },
                      })}
                      name="password"
                      type="password"
                      placeholder="Password*"
                      disabled={resetting}
                      className={clsx(
                        'w-full rounded-[6.5px] border-light-gray placeholder-dark-gray',
                        errors.password && 'border-red-500'
                      )}
                      onChange={({ target: { value } }) => {
                        const { message } = getStrengthCheckMessage(value)
                        setError('password', {
                          type: 'info',
                          message,
                        })
                      }}
                      onKeyPress={({ key, target: { value } }) => {
                        // React hook form doesn't capture input value yet. So, we have to manually set it in order for the form validation to work properly.
                        if (key === 'Enter') {
                          setValue('password', value)
                        }
                      }}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="password"
                      render={({ message }) => {
                        return errors.password.type === 'required' ? (
                          <p className="text-left text-xs text-red-500">
                            This is required
                          </p>
                        ) : (
                          <div
                            className="text-left text-xs"
                            dangerouslySetInnerHTML={{
                              __html: message,
                            }}
                          />
                        )
                      }}
                    />
                  </fieldset>

                  {/** Reset button */}
                  <input
                    type="submit"
                    value={resetting ? 'resetting...' : 'reset password'}
                    disabled={resetting}
                    className="w-full cursor-pointer rounded-full bg-skin-button-accent px-6 pb-3 pt-[0.6rem] font-bold text-white transition-colors group-[.resetting]:cursor-not-allowed group-[.resetting]:opacity-50 group-[:not(.resetting)]:hover:bg-skin-button-accent-hover group-[:not(.resetting)]:active:translate-y-[0.75px] group-[:not(.resetting)]:active:scale-[0.99]"
                  />
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

export default Reset
